/**
 * The planning run's self-review rounds, for the case file.
 *
 * One GET, `GET /api/work-items/:id/plan-self-review?planId=` →
 * {@link PlanSelfReviewResponse}. With a plan id the route reads the planning
 * run that produced that plan (an earlier revision has its own rounds);
 * without one, the ticket's live or latest planning run.
 *
 * Read when the item or the plan changes, then every {@link LIVE_RUN_POLL_MS}
 * while the response says the planning run is live, so the rounds fill in as
 * they land. The poll stops on the first read that is no longer live.
 *
 * A failed read is reported and renders nothing: the rounds are a record of
 * how the plan was made, never something the decision waits on. A failed
 * poll keeps what the last good read showed.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { PlanSelfReviewResponse } from "@yagni/shared";
import { captureUiError } from "../sentry.js";
import { LIVE_RUN_POLL_MS } from "./useWorkItemRuns.js";

/** The route's shape, or null: anything else is treated as nothing to show. */
function readResponse(body: unknown): PlanSelfReviewResponse | null {
  if (!body || typeof body !== "object" || Array.isArray(body)) return null;
  const candidate = body as Partial<PlanSelfReviewResponse>;
  if (typeof candidate.outcome !== "string" || !Array.isArray(candidate.rounds)) return null;
  return {
    planningRunId: typeof candidate.planningRunId === "string" ? candidate.planningRunId : null,
    outcome: candidate.outcome,
    live: candidate.live === true,
    rounds: candidate.rounds,
  };
}

export function usePlanSelfReview(
  workItemId: string,
  /** The plan revision on screen; null reads the latest planning run. */
  planId: string | null,
  /** False where no plan is drafted or decided: no request. */
  enabled: boolean,
): PlanSelfReviewResponse | null {
  const [review, setReview] = useState<PlanSelfReviewResponse | null>(null);
  // Only the newest request may land: a slow read for the previous revision
  // must never paint its rounds under the one now on screen.
  const latest = useRef(0);

  const read = useCallback(
    (background: boolean) => {
      const request = ++latest.current;
      const query = planId ? `?planId=${encodeURIComponent(planId)}` : "";
      fetch(`/api/work-items/${encodeURIComponent(workItemId)}/plan-self-review${query}`, {
        credentials: "include",
      })
        .then((res) => {
          if (!res.ok) throw new Error(`plan self-review read failed (${res.status})`);
          return res.json() as Promise<unknown>;
        })
        .then((body) => {
          if (request === latest.current) setReview(readResponse(body));
        })
        .catch((error: unknown) => {
          if (request !== latest.current || background) return;
          // A first read that failed shows nothing. A poll that failed keeps
          // the last good read on screen and tries again on the next tick, so
          // only the first read reports: a flaky network must not file one
          // report every fifteen seconds.
          setReview(null);
          captureUiError(error, {
            area: "Work",
            operation: "read_plan_self_review",
            severity: "warning",
          });
        });
    },
    [workItemId, planId],
  );

  useEffect(() => {
    if (!enabled) {
      latest.current += 1;
      setReview(null);
      return;
    }
    setReview(null);
    read(false);
  }, [enabled, read]);

  const live = enabled && review?.live === true;
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => read(true), LIVE_RUN_POLL_MS);
    return () => clearInterval(timer);
  }, [live, read]);

  return review;
}
